import { Component, inject, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NewUiButton } from "@ankaadia/ankaadia-shared-frontend";
import { CandidateStatus } from "@ankaadia/graphql";
import { TranslocoService } from "@jsverse/transloco";
import { derivedAsync } from "ngxtension/derived-async";
import { UIChart } from "primeng/chart";
import { MultiSelect } from "primeng/multiselect";
import { Popover } from "primeng/popover";
import { Select } from "primeng/select";
import { Skeleton } from "primeng/skeleton";
import { ToggleSwitch } from "primeng/toggleswitch";
import { BehaviorSubject, catchError, map, Observable, of, Subject, switchMap } from "rxjs";
import { NewUiDropdownSortOnTopDirective } from "../../../shared/primeng/dropdown-sort-selected-value-on-top/new-ui-dropdown-sort-on-top.directive";
import { NewUiMultiSelectSortOnTopDirective } from "../../../shared/primeng/multi-select-sort-selected-values-on-top/new-ui-multi-select-sort-on-top.directive";
import { SettingsService } from "../../../shared/services/settings.service";
import { TranslateDirective } from "../../../shared/transloco/translate.directive";
import { NewUiDashboardCardHeaderComponent } from "../layout/new-ui-dashboard-card-header/new-ui-dashboard-card-header.component";
import { NewUiDashboardCardComponent } from "../layout/new-ui-dashboard-card/new-ui-dashboard-card.component";
import { NewUiDashboardService } from "../new-ui-dashboard.service";
import { NewUiStateItemComponent } from "./state-item/new-ui-state-item.component";

@Component({
  selector: "new-ui-dashboard-candidate-status",
  templateUrl: "./new-ui-dashboard-candidate-status.component.html",
  imports: [
    NewUiDashboardCardComponent,
    NewUiDashboardCardHeaderComponent,
    TranslateDirective,
    ToggleSwitch,
    Select,
    NewUiDropdownSortOnTopDirective,
    UIChart,
    FormsModule,
    NewUiStateItemComponent,
    NewUiButton,
    Popover,
    MultiSelect,
    NewUiMultiSelectSortOnTopDirective,
    Skeleton,
  ],
})
export class NewUiDashboardCandidateStatusComponent {
  private readonly dashboardService = inject(NewUiDashboardService);
  protected readonly settings = inject(SettingsService);
  private readonly transloco = inject(TranslocoService);

  private readonly statusColors: Record<CandidateStatus, string> = {
    [CandidateStatus.New]: "#3B82F6",
    [CandidateStatus.Matched]: "#10B981",
    [CandidateStatus.ForSelection]: "#F59E0B",
    [CandidateStatus.Rejected]: "#EF4444",
    [CandidateStatus.Transferred]: "#8B5CF6",
    [CandidateStatus.Approved]: "#06B6D4",
    [CandidateStatus.Associated]: "#6B7280",
    [CandidateStatus.DroppedOut]: "#DC2626",
    [CandidateStatus.InRecognition]: "#14B8A6",
    [CandidateStatus.Inactive]: "#4B5563",
    [CandidateStatus.LabourMigrationFinished]: "#22C55E",
    [CandidateStatus.Paused]: "#FBBF24",
    [CandidateStatus.Unknown]: "#374151",
    [CandidateStatus.Archived]: "#9CA3AF",
  };

  protected readonly showHistory = signal<boolean>(false);
  protected readonly selectedHistory = signal<number>(7);
  protected readonly historyOptions = [
    { label: this.transloco.translate("common.dateRanges.last7Days"), value: 7 },
    { label: this.transloco.translate("common.dateRanges.last30Days"), value: 30 },
    { label: this.transloco.translate("common.dateRanges.last90Days"), value: 90 },
    { label: this.transloco.translate("common.dateRanges.last180Days"), value: 180 },
    { label: this.transloco.translate("common.dateRanges.last365Days"), value: 365 },
  ];

  protected readonly selectedStates = signal<CandidateStatus[]>([
    CandidateStatus.New,
    CandidateStatus.Matched,
    CandidateStatus.ForSelection,
    CandidateStatus.Rejected,
    CandidateStatus.Transferred,
  ]);

  protected readonly shownStates = signal(
    Object.keys(CandidateStatus).map((key) => ({
      label: this.transloco.translate(`enum.CandidateStatus.${key}`),
      value: CandidateStatus[key as keyof typeof CandidateStatus],
    }))
  );

  protected readonly options = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  updateHistoryData(): void {
    if (this.showHistory()) {
      this.historyDataSubject.next();
    }
  }

  getDiff(key: string, data: { keys: string[]; labels: string[]; datasets: { data: number[] }[] }): number {
    const position = data.keys.indexOf(key);
    if (position === -1) {
      return null;
    }
    return data.datasets[0].data[position];
  }

  protected readonly historyDataSubject = new Subject<void>();

  protected readonly historyData$: Observable<{
    keys: string[];
    labels: string[];
    datasets: { data: number[]; backgroundColor: string[] }[];
  }> = this.historyDataSubject.pipe(
    switchMap(() =>
      this.dashboardService.getCandidateStatesAtDate({
        organizationId: this.settings.organizationId,
        daysBefore: this.selectedHistory(),
        states: this.selectedStates(),
      })
    ),
    map((stateData) => ({
      keys: stateData.map((state) => state.state),
      labels: stateData.map((state) => this.transloco.translate(`enum.CandidateStatus.${state.state}`)),
      datasets: [
        {
          data: stateData.map((state) => state.count),
          backgroundColor: stateData.map((state) => this.statusColors[state.state]),
        },
      ],
    }))
  );

  protected readonly dataSubject = new BehaviorSubject<CandidateStatus[]>(this.selectedStates());
  protected readonly data$ = this.dataSubject.pipe(
    switchMap((states) =>
      this.dashboardService.getCandidateStates({ organizationId: this.settings.organizationId, states: states })
    ),
    map((stateData) => ({
      keys: stateData.map((state) => state.state),
      labels: stateData.map((state) => this.transloco.translate(`enum.CandidateStatus.${state.state}`)),
      datasets: [
        {
          data: stateData.map((state) => state.count),
          backgroundColor: stateData.map((state) => this.statusColors[state.state]),
        },
      ],
    }))
  );

  protected readonly data = derivedAsync(() => this.data$.pipe(catchError(() => of(null))));
  protected readonly historyData = derivedAsync(() => this.historyData$.pipe(catchError(() => of(null))));
}
